import { Component, DestroyRef, forwardRef, HostBinding, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	AbstractControl,
	ControlValueAccessor,
	FormControl,
	FormGroup,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	ReactiveFormsModule,
	ValidationErrors,
	Validator,
	Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { FormGroupComponent, InputComponent, ISelect, SelectComponent } from '@tecalisdev/infinity';
import {
	formatRetentionDuration,
	isTimedRetentionUnit,
	parseRetentionDuration,
	RetentionDurationApiValue,
	RetentionDurationSelectUnit,
} from '../../utils/retention-duration.util';

@Component({
	selector: 'app-retention-duration-input',
	imports: [ReactiveFormsModule, FormGroupComponent, SelectComponent, InputComponent],
	templateUrl: './retention-duration-input.component.html',
	styleUrl: './retention-duration-input.component.scss',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => RetentionDurationInputComponent),
			multi: true,
		},
		{
			provide: NG_VALIDATORS,
			useExisting: forwardRef(() => RetentionDurationInputComponent),
			multi: true,
		},
	],
})
export class RetentionDurationInputComponent implements ControlValueAccessor, Validator, OnInit {
	@Input() label = '';
	@Input() hasError = false;

	/** Con label (workflows/grid): bloque al 100%. Sin label (compañías/actions): inline a la derecha. */
	@HostBinding('class.retention-duration-input--inline')
	get isInline(): boolean {
		return !this.label;
	}

	private readonly destroyRef = inject(DestroyRef);
	private readonly translate = inject(TranslateService);
	private syncingExternalValue = false;

	public unitOptions: ISelect[] = [];
	public internalForm = new FormGroup({
		unit: new FormControl<ISelect | null>(null),
		amount: new FormControl<number | null>(null),
	});

	private onChange: (value: RetentionDurationApiValue) => void = () => undefined;
	private onTouched: () => void = () => undefined;
	private onValidatorChange: () => void = () => undefined;

	ngOnInit(): void {
		this.refreshUnitOptions();
		this.ensureDefaultUnit();
		this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.refreshUnitOptions();
			this.syncUnitSelectFromId(this.currentUnitId());
		});

		this.internalForm
			.get('unit')
			?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				if (this.syncingExternalValue) {
					return;
				}
				this.syncAmountValidators();
				this.emitValue();
				this.onValidatorChange();
			});

		this.internalForm
			.get('amount')
			?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				if (this.syncingExternalValue) {
					return;
				}
				this.emitValue();
				this.onValidatorChange();
			});
	}

	writeValue(value: RetentionDurationApiValue | number | undefined): void {
		this.syncingExternalValue = true;
		const parts = parseRetentionDuration(value);
		this.syncUnitSelectFromId(parts.unit);
		this.internalForm.patchValue({ amount: parts.amount }, { emitEvent: false });
		this.syncAmountValidators();
		this.syncingExternalValue = false;
	}

	registerOnChange(fn: (value: RetentionDurationApiValue) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	registerOnValidatorChange(fn: () => void): void {
		this.onValidatorChange = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		if (isDisabled) {
			this.internalForm.disable({ emitEvent: false });
			return;
		}
		this.internalForm.enable({ emitEvent: false });
		this.syncAmountValidators();
	}

	validate(_control: AbstractControl): ValidationErrors | null {
		const unit = this.currentUnitId();
		if (!isTimedRetentionUnit(unit)) {
			return null;
		}
		const amountCtrl = this.internalForm.get('amount');
		if (amountCtrl?.errors?.['required'] || amountCtrl?.errors?.['min'] || amountCtrl?.errors?.['retentionInteger']) {
			return { retentionDuration: true };
		}
		const raw = amountCtrl?.value;
		const n = typeof raw === 'number' ? raw : Number(raw);
		if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) {
			return { retentionDuration: true };
		}
		return null;
	}

	public showAmountInput(): boolean {
		return isTimedRetentionUnit(this.currentUnitId());
	}

	public markAsTouched(): void {
		this.internalForm.markAllAsTouched();
		this.onTouched();
	}

	public amountHasError(): boolean {
		const control = this.internalForm.get('amount');
		return !!control && control.touched && control.invalid;
	}

	public amountErrorMsg(): string {
		const control = this.internalForm.get('amount');
		if (!this.showAmountInput()) {
			return '';
		}
		if (!this.hasError && !(control?.touched && control.invalid)) {
			return '';
		}
		if (control?.errors?.['required'] || control?.errors?.['min'] || this.hasError) {
			if (control?.errors?.['retentionInteger']) {
				return this.translate.instant('SHARED.RETENTION.AMOUNT_INTEGER_ERROR');
			}
			return this.translate.instant('SHARED.RETENTION.AMOUNT_MIN_ERROR');
		}
		if (control?.errors?.['retentionInteger']) {
			return this.translate.instant('SHARED.RETENTION.AMOUNT_INTEGER_ERROR');
		}
		return '';
	}

	private currentUnitId(): RetentionDurationSelectUnit {
		const selected = this.internalForm.get('unit')?.value;
		if (selected && typeof selected === 'object' && 'id' in selected) {
			return String(selected.id) as RetentionDurationSelectUnit;
		}
		return 'none';
	}

	private refreshUnitOptions(): void {
		this.unitOptions = [
			{ id: 'none', label: this.translate.instant('SHARED.RETENTION.NONE') },
			{ id: 'immediate', label: this.translate.instant('SHARED.RETENTION.IMMEDIATE') },
			{ id: 'm', label: this.translate.instant('SHARED.RETENTION.MINUTES') },
			{ id: 'h', label: this.translate.instant('SHARED.RETENTION.HOURS') },
			{ id: 'd', label: this.translate.instant('SHARED.RETENTION.DAYS') },
			{ id: 'a', label: this.translate.instant('SHARED.RETENTION.YEARS') },
		];
	}

	private ensureDefaultUnit(): void {
		if (!this.internalForm.get('unit')?.value) {
			this.syncUnitSelectFromId('none');
		}
	}

	private syncUnitSelectFromId(unitId: RetentionDurationSelectUnit): void {
		const option = this.unitOptions.find((item) => item.id === unitId) ?? this.unitOptions[0];
		this.internalForm.patchValue({ unit: option }, { emitEvent: false });
	}

	private syncAmountValidators(): void {
		const amountCtrl = this.internalForm.get('amount');
		if (!amountCtrl) {
			return;
		}
		if (isTimedRetentionUnit(this.currentUnitId())) {
			amountCtrl.setValidators([Validators.required, Validators.min(1), this.positiveIntegerValidator]);
			if (amountCtrl.value == null || amountCtrl.value === ('' as unknown)) {
				amountCtrl.setValue(1, { emitEvent: false });
			}
		} else {
			amountCtrl.clearValidators();
			amountCtrl.setValue(null, { emitEvent: false });
		}
		amountCtrl.updateValueAndValidity({ emitEvent: false });
	}

	private positiveIntegerValidator = (control: AbstractControl): ValidationErrors | null => {
		const raw = control.value;
		if (raw === null || raw === undefined || raw === '') {
			return null;
		}
		const n = typeof raw === 'number' ? raw : Number(raw);
		if (!Number.isFinite(n) || !Number.isInteger(n)) {
			return { retentionInteger: true };
		}
		return null;
	};

	private emitValue(): void {
		const unit = this.currentUnitId();
		const amount = this.internalForm.get('amount')?.value;
		this.onChange(formatRetentionDuration(unit, amount));
		this.onTouched();
	}
}
